import { useEffect, useRef } from 'react';

import { useAuth } from '@/hooks/use-auth';
import { logger } from '@/utils/logger';

const CONSENT_PREFIX = 'mora:webmcp-consent:v1:';
const TOOL_NAME = 'get_brand_profile';

type WebMcpAnnotations = {
  readOnlyHint: true;
  destructiveHint: false;
  idempotentHint: true;
  openWorldHint: false;
};

type WebMcpTool = {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
  annotations: WebMcpAnnotations;
  execute: () => Promise<unknown>;
};

type ModelContext = {
  registerTool: (tool: WebMcpTool, options?: { signal?: AbortSignal }) => Promise<void>;
};

type DocumentWithModelContext = Document & {
  modelContext?: ModelContext;
};

function consentKey(userId: string) {
  return `${CONSENT_PREFIX}${userId}`;
}

function hasConsent(userId: string): boolean {
  try {
    return window.localStorage.getItem(consentKey(userId)) === 'granted';
  } catch {
    return false;
  }
}

export function getWebMCPConsent(userId: string): boolean {
  return hasConsent(userId);
}

function hasDecision(userId: string): boolean {
  try {
    return window.localStorage.getItem(consentKey(userId)) !== null;
  } catch {
    return false;
  }
}

function saveConsent(userId: string, granted: boolean) {
  try {
    window.localStorage.setItem(consentKey(userId), granted ? 'granted' : 'denied');
  } catch {
    // A storage failure must fail closed; the tool stays unregistered.
  }
}

/** Call from account-security UI when the user wants to revoke browser-agent access. */
export function revokeWebMCPConsent(userId: string) {
  try {
    window.localStorage.removeItem(consentKey(userId));
    window.dispatchEvent(new CustomEvent('mora:webmcp-revoke', { detail: { userId } }));
  } catch {
    // Storage may be unavailable in a privacy-restricted browser.
  }
}

async function readBrandProfile(accessToken: string) {
  const response = await fetch('/api/mcp', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      jsonrpc: '2.0',
      id: crypto.randomUUID(),
      method: 'tools/call',
      params: { name: TOOL_NAME, arguments: {} },
    }),
  });

  if (!response.ok) {
    throw new Error(`Mora MCP returned HTTP ${response.status}`);
  }

  const payload = (await response.json()) as {
    error?: { message?: string };
    result?: unknown;
  };
  if (payload.error) throw new Error(payload.error.message ?? 'Mora MCP tool failed');
  return payload.result;
}

/**
 * Registers one real, read-only, account-scoped WebMCP tool after explicit
 * browser consent. The current WebMCP draft exposes document.modelContext;
 * the old navigator.modelContext.provideContext API is intentionally not used.
 */
export function useWebMCP() {
  const { user, session, loading } = useAuth();
  const sessionRef = useRef(session);
  const userRef = useRef(user);
  const registeredRef = useRef(false);
  const consentRef = useRef(false);
  const registrationAbortRef = useRef<AbortController | null>(null);

  sessionRef.current = session;
  userRef.current = user;

  useEffect(() => {
    const onRevoke = (event: Event) => {
      const detail = (event as CustomEvent<{ userId?: string }>).detail;
      if (detail?.userId === userRef.current?.id) {
        consentRef.current = false;
        registrationAbortRef.current?.abort();
        registrationAbortRef.current = null;
        registeredRef.current = false;
      }
    };
    window.addEventListener('mora:webmcp-revoke', onRevoke);
    return () => window.removeEventListener('mora:webmcp-revoke', onRevoke);
  }, []);

  useEffect(() => {
    if (loading || !user?.id || !session?.access_token || typeof document === 'undefined') return;

    const modelContext = (document as DocumentWithModelContext).modelContext;
    if (!modelContext?.registerTool) return;

    if (!hasDecision(user.id)) {
      const granted = window.confirm(
        'Allow browser AI clients to read your Mora brand profile? This is read-only, account-scoped, and can be revoked from account security settings.',
      );
      saveConsent(user.id, granted);
    }

    consentRef.current = hasConsent(user.id);
    if (!consentRef.current || registeredRef.current) return;

    const registrationAbortController = new AbortController();
    registrationAbortRef.current = registrationAbortController;

    void modelContext
      .registerTool(
        {
          name: TOOL_NAME,
          description:
            'Read the signed-in Mora account brand profile. Returns no write actions and never reads another account.',
          inputSchema: {
            type: 'object',
            properties: {},
            additionalProperties: false,
          },
          annotations: {
            readOnlyHint: true,
            destructiveHint: false,
            idempotentHint: true,
            openWorldHint: false,
          },
          execute: async () => {
            const currentUser = userRef.current;
            const currentSession = sessionRef.current;
            if (!currentUser || !currentSession?.access_token || !consentRef.current) {
              throw new Error('WebMCP consent is required for Mora account data');
            }
            return readBrandProfile(currentSession.access_token);
          },
        },
        { signal: registrationAbortController.signal },
      )
      .then(() => {
        if (registrationAbortController.signal.aborted) return;
        registeredRef.current = true;
      })
      .catch((error: unknown) => {
        if (registrationAbortController.signal.aborted) return;
        logger.warn('Failed to register WebMCP tool', error);
      });

    return () => {
      consentRef.current = false;
      registrationAbortController.abort();
      if (registrationAbortRef.current === registrationAbortController) {
        registrationAbortRef.current = null;
      }
      registeredRef.current = false;
    };
  }, [loading, session?.access_token, user?.id]);
}
